import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

export function OutboundPayoutsTile() {
  const [status, setStatus] = useState(null);
  const [list, setList] = useState(null);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/payouts/status"),
        api.get("/admin/payouts/list?limit=20"),
      ]);
      setStatus(s.data);
      setList(l.data);
    } catch {
      // Silent — admin-only endpoint, parent handles auth.
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, []);

  if (!status) return null;

  const enabled = status.enabled;
  const reachable = status.healthcheck?.reachable;
  const dotColor = enabled
    ? reachable === true ? "bg-emerald-500" : reachable === false ? "bg-rose-500" : "bg-amber-500"
    : "bg-stone-400";
  const items = list?.items || [];
  const byStatus = list?.by_status || {};

  return (
    <div className="border border-stone-200 rounded-xl p-5 bg-white" data-testid="outbound-payouts-tile">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${dotColor} ${enabled && reachable === false ? "animate-pulse" : ""}`} />
          <h3 className="text-sm font-semibold tracking-wide uppercase text-stone-700">
            Outbound Payouts · Stripe Global Payouts
          </h3>
          <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full ${enabled ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>
            {enabled ? "ACTIVE" : "DORMANT"}
          </span>
          <span className="text-[10px] tracking-[0.15em] uppercase text-stone-500">Beta · Phase 1</span>
        </div>
        <button onClick={refresh} className="text-[11px] text-stone-500 hover:text-stone-800 underline" data-testid="payouts-refresh-btn">refresh</button>
      </div>

      {!enabled && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900" data-testid="payouts-dormant-banner">
          <p className="font-medium">Feature dormant — next step: email Stripe Sales.</p>
          <p className="mt-1 leading-relaxed">Copy the template below and send from your admin email (the one on your Stripe account). Typical response in 2-5 business days.</p>
          <div className="mt-2 rounded-md border border-amber-300 bg-white p-3 text-[11px] text-stone-800 font-mono leading-snug relative">
            <p><b>To:</b> sales@stripe.com</p>
            <p><b>Subject:</b> Request: Enable Global Payouts on our platform (Zcardon)</p>
            <div className="mt-2 whitespace-pre-wrap text-[11px] text-stone-700" data-testid="payouts-email-body">{`Hi Stripe Sales team,

We operate Zcardon — a remittance platform that lets customers fund direct deposits (debit/credit card via Stripe Checkout) and forwards the funds to receivers' bank accounts.

We'd like to enable Global Payouts on our platform account to automate outbound transfers to receivers' bank accounts, primarily in Vietnam (plus US, GB, and other countries in Southeast Asia).

Our platform is US-incorporated. Expected monthly outbound volume: $10k–$100k ramping. We've already integrated the Stripe v2 Money Management Outbound API (OutboundSetupIntent + OutboundPayment) against the 2026-04-22.preview version in our codebase — we just need your team to flip the capability on our account.

Could you point us at the right onboarding form / verification docs, and confirm our account qualifies? Happy to jump on a call.

Thanks,
[YOUR NAME]
Zcardon`}</div>
            <button
              onClick={() => {
                const body = document.querySelector('[data-testid="payouts-email-body"]');
                if (body && navigator.clipboard) {
                  navigator.clipboard.writeText(body.innerText).then(
                    () => toast.success("Email template copied to clipboard"),
                    () => toast.error("Could not copy"),
                  );
                }
              }}
              className="absolute top-2 right-2 text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="payouts-copy-email-btn"
            >
              Copy
            </button>
          </div>
          <p className="mt-2 text-[11px]">Once Stripe confirms, I'll walk you through setting <code>GLOBAL_PAYOUTS_ENABLED=true</code> and wiring the webhook secret — the rest is already built.</p>
        </div>
      )}

      {enabled && reachable === false && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-900" data-testid="payouts-unreachable-banner">
          <p className="font-medium">Stripe v2 Money Management is not reachable.</p>
          <p className="mt-1 break-words">{status.healthcheck?.error || "Unknown error"}</p>
          <p className="mt-1 text-[11px] text-rose-700">This usually means Stripe hasn't yet enabled Global Payouts on your platform. Contact Stripe Sales to confirm.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="payouts-kpi-version">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500">API version</p>
          <p className="text-[13px] font-mono mt-1 text-stone-800 break-all">{status.api_version}</p>
        </div>
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="payouts-kpi-countries">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500">Supported destinations</p>
          <p className="text-[13px] mt-1 text-stone-800">{(status.supported_countries || []).length} countries</p>
          <p className="text-[10px] mt-0.5 text-stone-500">{(status.supported_countries || []).join(" · ")}</p>
        </div>
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="payouts-kpi-totals">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500">Recent payouts</p>
          <p className="text-2xl font-semibold mt-0.5">{list?.count ?? 0}</p>
          <p className="text-[10px] mt-0.5 text-stone-500">
            {Object.entries(byStatus).map(([k, v]) => `${k}:${v}`).join(" · ") || "none yet"}
          </p>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-4 border-t border-stone-200 pt-3">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-2">Recent</p>
          <div className="space-y-1.5">
            {items.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-[12px]" data-testid={`payout-row-${p.id}`}>
                <div>
                  <span className="font-mono text-stone-700">{(p.stripe_payout_id || "").slice(0, 18) || "—"}</span>
                  <span className="ml-2 text-stone-500">{p.currency} {Number(p.amount || 0).toFixed(2)}</span>
                </div>
                <span className={`text-[10px] tracking-[0.2em] uppercase px-1.5 py-0.5 rounded ${
                  p.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                  p.status === "failed" || p.status === "returned" ? "bg-rose-100 text-rose-800" :
                  p.status === "canceled" ? "bg-stone-100 text-stone-600" :
                  "bg-amber-100 text-amber-800"
                }`}>
                  {p.status || "pending"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
